import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, RefreshCw, Search, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { MessageModal } from "../../components/ui/MessageModal";
import { ScrollShadow } from "../../components/ui/ScrollShadow";
import { ErrorTile, Td, Th, fmtDateTime } from "../../components/reports/ReportBits";
import { useLivePolling } from "../../hooks/useLivePolling";
import { useSession } from "../../hooks/useSession";
import {
  banlistError,
  pbxBanlistApi,
  type BanAction,
  type Banlist,
  type BannedIp,
  type Deploy,
  type ExtensionRef,
  type IgnoreList,
  type IpLookup,
  type LiveIgnore,
  type LogTool,
  type ToolOutput,
  type TriedUser,
} from "../../api/pbxBanlistApi";
import { Pill, SegmentedFilter, fmtAgo, inputStyle, monoStyle, secondsSince } from "./pbxExtensionBits";

type Tab = "jails" | "lookup" | "logs" | "ignore" | "activity";
const TABS: { value: Tab; label: string }[] = [
  { value: "jails", label: "Jails" },
  { value: "lookup", label: "Lookup" },
  { value: "logs", label: "Logs" },
  { value: "ignore", label: "Ignore list" },
  { value: "activity", label: "Activity" },
];
const POLL_MS = 15000;
const TZ = "UTC";

/** "in 12 m", "in 3 d" for a future instant. */
function fmtIn(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return "—";
  const s = (t - Date.now()) / 1000;
  if (s <= 0) return "now";
  return fmtAgo(s).replace(/ ago$/, "").replace(/^/, "in ");
}

function fmtUtc(iso: string | null | undefined): string {
  return iso ? fmtDateTime(iso, TZ) : "—";
}

function ExtensionLabel({ ext }: { ext: ExtensionRef }) {
  return (
    <Link to={`/ippbx/extensions/${ext.agentId}`} title={`${ext.customerName} · ${ext.pbxUsername}`}>
      {ext.agentName || ext.pbxUsername}
    </Link>
  );
}

function UserChips({ users, otherUsers }: { users: TriedUser[]; otherUsers: number }) {
  if (users.length === 0 && otherUsers === 0) return <span style={{ color: "var(--portal-muted)" }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 360 }}>
      {users.slice(0, 6).map((u) => (
        <span key={u.username} title={`${u.failed} failed of ${u.attempts} attempts · last ${fmtUtc(u.lastUtc)}${u.extension ? ` · ${u.extension.customerName}` : ""}`}>
          <Pill tone={u.extension ? "warn" : "muted"}>
            <span style={monoStyle}>{u.username}</span> ×{u.failed}
          </Pill>
        </span>
      ))}
      {users.length > 6 && <Pill tone="muted">+{users.length - 6}</Pill>}
      {otherUsers > 0 && <Pill tone="muted">{otherUsers} other{otherUsers === 1 ? "" : "s"}</Pill>}
    </div>
  );
}

function DeployLines({ deploys }: { deploys: Deploy[] }) {
  if (deploys.length === 0) return null;
  return (
    <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
      {deploys.map((d) => (
        <li key={d.pbxId}>
          {d.hostname}:{" "}
          {d.error
            ? `failed: ${d.error}`
            : d.skipped
              ? d.skipReason === "not_deployed_yet"
                ? "not applied yet; an admin does the first deploy of the ignore list"
                : d.skipReason === "in_sync"
                  ? "already up to date"
                  : d.skipReason
              : `applied (${d.entries} entries${d.unbanned.length ? `; unbanned ${d.unbanned.join(", ")}` : ""})`}
        </li>
      ))}
    </ul>
  );
}

/**
 * IPPBX → Banlist: fail2ban on the PBXs. Jails, lookup of one address,
 * iplog / userlog, unban and the ignore list. Admins see everything,
 * managers the addresses that belong to their extensions.
 */
export function PbxBanlistPage() {
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "jails") as Tab;

  const go = useCallback(
    (next: Record<string, string | null>) => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(next)) if (v) sp.set(k, v);
      setParams(sp, { replace: false });
    },
    [setParams],
  );

  const { data, loading, refreshing, error, refresh } = useLivePolling<Banlist>({
    fetcher: (signal) => pbxBanlistApi.list(false, signal),
    intervalMs: POLL_MS,
    enabled: !!session?.isAuthenticated,
  });
  const [forcing, setForcing] = useState(false);
  const forceRefresh = async () => {
    setForcing(true);
    try { await pbxBanlistApi.list(true); await refresh(); } finally { setForcing(false); }
  };

  // ---- unban dialog (shared by jails and lookup) ---------------------------------------
  const [unbanIp, setUnbanIp] = useState<string | null>(null);
  const [alsoIgnore, setAlsoIgnore] = useState(false);
  const [ignoreComment, setIgnoreComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ title: string; body: ReactNode } | null>(null);

  const askUnban = (ip: string, ignore: boolean) => {
    setUnbanIp(ip);
    setAlsoIgnore(ignore);
    setIgnoreComment("");
  };
  const doUnban = async () => {
    if (!unbanIp) return;
    setBusy(true);
    try {
      const r = await pbxBanlistApi.unban({ ip: unbanIp, ignore: alsoIgnore, comment: ignoreComment.trim() || undefined });
      setMessage({
        title: `${r.ip} unbanned`,
        body: (
          <>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {r.results.map((p) => (
                <li key={p.pbxId}>
                  {p.hostname}: {p.error ? `failed: ${p.error}` : p.unbannedFrom.length ? `released from ${p.unbannedFrom.join(", ")}` : "was not banned"}
                </li>
              ))}
            </ul>
            {r.ignoreEntry && <p style={{ margin: "10px 0 0" }}>Added to the ignore list as <span style={monoStyle}>{r.ignoreEntry.cidr}</span>.</p>}
            {r.ignoreNote && <p style={{ margin: "10px 0 0" }}>{r.ignoreNote}</p>}
            <DeployLines deploys={r.deploys} />
          </>
        ),
      });
      void refresh();
    } catch (e) {
      setMessage({ title: "Unban failed", body: banlistError(e) });
    } finally {
      setBusy(false);
      setUnbanIp(null);
    }
  };

  const pbxs = data?.pbxs ?? [];
  const isAdmin = data?.isAdmin ?? session?.kind === "Admin";

  return (
    <>
      <PageHeader
        title="IPPBX banlist"
        actions={
          <button type="button" className="btn btn-secondary" onClick={forceRefresh} disabled={forcing} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={14} /> {forcing ? "Refreshing…" : "Refresh now"}
          </button>
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Addresses fail2ban has jailed on the PBXs after failed registrations, with the usernames they tried.
          {isAdmin ? "" : " You see the addresses your extensions registered from or that tried one of your usernames."} Times are UTC.
        </p>

        {data && !data.sshConfigured && <ErrorTile message="The PBX SSH password (PbxSsh:Password) is not configured, so the PBXs cannot be reached." />}
        {error != null && <ErrorTile message="The banlist could not be refreshed. Showing the last known data." />}

        {pbxs.length > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {pbxs.map((p) => (
              <div key={p.pbxId} className="tile" style={{ padding: "8px 14px", gap: 2, borderColor: p.ok ? undefined : "#e74c3c" }}>
                <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: p.ok ? (refreshing ? "var(--portal-warn)" : "var(--portal-status-success)") : "#e74c3c" }} />
                  {p.hostname}
                </div>
                <div style={{ fontSize: "0.78rem", color: p.ok ? "var(--portal-muted)" : "#a12a1f" }}>
                  {p.ok
                    ? `${p.jails.map((j) => `${j.jail} ${j.banned}`).join(" · ")}${p.statusUtc ? ` · ${fmtAgo(secondsSince(p.statusUtc))}` : ""}`
                    : p.error}
                </div>
              </div>
            ))}
          </div>
        )}

        <section className="tile" style={{ display: "grid", gap: 12 }}>
          <div className="pbx-tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.value} type="button" role="tab" aria-selected={tab === t.value} className="pbx-tab" onClick={() => go({ tab: t.value === "jails" ? null : t.value })}>
                {t.label}
                {t.value === "jails" && data ? <span style={{ marginLeft: 6, opacity: 0.7 }}>{data.items.length}</span> : null}
              </button>
            ))}
          </div>

          {tab === "jails" && (
            <JailsTab
              data={data}
              loading={loading}
              onLookup={(ip) => go({ tab: "lookup", ip })}
              onLog={(ip, pbxId) => go({ tab: "logs", tool: "iplog", target: ip, pbx: String(pbxId) })}
              onUnban={askUnban}
            />
          )}
          {tab === "lookup" && (
            <LookupTab
              ip={params.get("ip") ?? ""}
              onSearch={(ip) => go({ tab: "lookup", ip })}
              onLog={(tool, target, pbxId) => go({ tab: "logs", tool, target, pbx: String(pbxId) })}
              onUnban={askUnban}
            />
          )}
          {tab === "logs" && (
            <LogsTab
              pbxs={pbxs.map((p) => ({ pbxId: p.pbxId, hostname: p.hostname }))}
              tool={(params.get("tool") as LogTool) || "iplog"}
              target={params.get("target") ?? ""}
              pbxId={params.get("pbx") ? Number(params.get("pbx")) : null}
              onRun={(tool, target, pbxId) => go({ tab: "logs", tool, target, pbx: String(pbxId) })}
            />
          )}
          {tab === "ignore" && <IgnoreTab pbxs={pbxs.map((p) => ({ pbxId: p.pbxId, hostname: p.hostname }))} />}
          {tab === "activity" && <ActivityTab />}
        </section>
      </div>

      <ConfirmModal
        open={unbanIp != null}
        onClose={() => (busy ? undefined : setUnbanIp(null))}
        title={alsoIgnore ? "Unban and ignore" : "Unban"}
        confirmLabel={alsoIgnore ? "Unban and ignore" : "Unban"}
        tone="caution"
        busy={busy}
        onConfirm={doUnban}
      >
        <div style={{ display: "grid", gap: 10 }}>
          <p style={{ margin: 0 }}>
            Release <span style={monoStyle}>{unbanIp}</span> from every jail on every PBX? Without the ignore list it is jailed again after the next failed registrations.
          </p>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={alsoIgnore} onChange={(e) => setAlsoIgnore(e.target.checked)} />
            Also add it to the ignore list (never jailed, and not blocked by the ipsum list)
          </label>
          {alsoIgnore && (
            <input
              type="text"
              value={ignoreComment}
              onChange={(e) => setIgnoreComment(e.target.value)}
              placeholder="Why (optional), e.g. office of customer X"
              maxLength={200}
              style={{ ...inputStyle, width: "100%" }}
              aria-label="Comment"
            />
          )}
        </div>
      </ConfirmModal>
      <MessageModal open={message != null} onClose={() => setMessage(null)} title={message?.title ?? ""}>
        <div>{message?.body}</div>
      </MessageModal>
    </>
  );
}

// ---- jails ------------------------------------------------------------------------------

function JailsTab({ data, loading, onLookup, onLog, onUnban }: {
  data: Banlist | null;
  loading: boolean;
  onLookup: (ip: string) => void;
  onLog: (ip: string, pbxId: number) => void;
  onUnban: (ip: string, ignore: boolean) => void;
}) {
  const [jail, setJail] = useState("all");
  const [search, setSearch] = useState("");
  const items = data?.items ?? [];
  const multiPbx = (data?.pbxs.length ?? 0) > 1;

  const jailOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of items) counts.set(i.jail, (counts.get(i.jail) ?? 0) + 1);
    return [{ value: "all", label: "All", count: items.length }, ...[...counts.entries()].sort().map(([j, n]) => ({ value: j, label: j, count: n }))];
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i: BannedIp) =>
      (jail === "all" || i.jail === jail) &&
      (!q || [i.ip, i.hostname, ...i.users.map((u) => u.username), ...i.users.map((u) => u.extension?.customerName ?? ""), ...i.registeredFrom.map((r) => `${r.agentName} ${r.customerName}`)]
        .some((v) => v.toLowerCase().includes(q))));
  }, [items, jail, search]);

  if (loading && !data) return <div style={{ color: "var(--portal-muted)" }}>Loading jails…</div>;

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <SegmentedFilter value={jail} options={jailOptions} onChange={setJail} ariaLabel="Jail" />
        <label style={{ position: "relative", flex: "1 1 220px", maxWidth: 360 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--portal-muted)" }} />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search IP, username, customer…" style={{ ...inputStyle, width: "100%", paddingLeft: 30 }} aria-label="Search" />
        </label>
      </div>
      {filtered.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>{items.length === 0 ? "Nothing is jailed right now." : "No jailed address matches the filter."}</div>
      ) : (
        <ScrollShadow>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr>
                <Th>Address</Th>
                <Th>Jail</Th>
                {multiPbx && <Th>PBX</Th>}
                <Th>Jailed</Th>
                <Th>Released</Th>
                <Th>Tried usernames</Th>
                <Th>Registered as</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={`${i.pbxId}-${i.jail}-${i.ip}`}>
                  <Td><span style={monoStyle}>{i.ip}</span></Td>
                  <Td><Pill tone={i.jail === "recidive" ? "warn" : "info"}>{i.jail}</Pill></Td>
                  {multiPbx && <Td>{i.hostname}</Td>}
                  <Td><span title={fmtUtc(i.bannedUtc)}>{i.bannedUtc ? fmtAgo(secondsSince(i.bannedUtc)) : "—"}</span></Td>
                  <Td><span title={fmtUtc(i.endsUtc)}>{fmtIn(i.endsUtc)}</span></Td>
                  <Td><UserChips users={i.users} otherUsers={i.otherUsers} /></Td>
                  <Td>
                    {i.registeredFrom.length === 0 ? <span style={{ color: "var(--portal-muted)" }}>—</span> : (
                      <span>{i.registeredFrom.slice(0, 3).map((r, n) => <span key={r.agentId}>{n > 0 && ", "}<ExtensionLabel ext={r} /></span>)}{i.registeredFrom.length > 3 && ` +${i.registeredFrom.length - 3}`}</span>
                    )}
                  </Td>
                  <Td align="right">
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button type="button" className="btn btn-secondary" title="Look up this address" aria-label="Look up" onClick={() => onLookup(i.ip)} style={{ padding: "4px 8px" }}><Search size={14} /></button>
                      <button type="button" className="btn btn-secondary" title="iplog" aria-label="iplog" onClick={() => onLog(i.ip, i.pbxId)} style={{ padding: "4px 8px" }}><FileText size={14} /></button>
                      <button type="button" className="btn btn-secondary" title="Unban" aria-label="Unban" onClick={() => onUnban(i.ip, false)} style={{ padding: "4px 8px" }}><ShieldOff size={14} /></button>
                      <button type="button" className="btn btn-secondary" title="Unban and add to the ignore list" aria-label="Unban and ignore" onClick={() => onUnban(i.ip, true)} style={{ padding: "4px 8px" }}><ShieldCheck size={14} /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      )}
    </div>
  );
}

// ---- lookup -----------------------------------------------------------------------------

function LookupTab({ ip, onSearch, onLog, onUnban }: {
  ip: string;
  onSearch: (ip: string) => void;
  onLog: (tool: LogTool, target: string, pbxId: number) => void;
  onUnban: (ip: string, ignore: boolean) => void;
}) {
  const [input, setInput] = useState(ip);
  const [result, setResult] = useState<IpLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setInput(ip);
    setResult(null);
    setError(null);
    if (!ip) return;
    let cancelled = false;
    setBusy(true);
    pbxBanlistApi.lookup(ip)
      .then((r) => { if (!cancelled) setResult(r); })
      .catch((e) => { if (!cancelled) setError(banlistError(e)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [ip]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (input.trim()) onSearch(input.trim());
  };

  const jailed = result?.pbxs.some((p) => p.jails.length > 0) ?? false;
  const inIpsum = result?.pbxs.some((p) => p.ipsum) ?? false;

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 12 }}>
      <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="IP address, e.g. 203.0.113.7" style={{ ...inputStyle, ...monoStyle, flex: "0 1 280px" }} aria-label="IP address" />
        <button type="submit" className="btn btn-primary" disabled={busy || !input.trim()}>{busy ? "Checking…" : "Check"}</button>
      </form>
      {error && <ErrorTile message={error} />}
      {result && !result.inScope && <div style={{ color: "var(--portal-muted)" }}>{result.ip} has not registered as, or tried, any of your extensions.</div>}
      {result && result.inScope && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ ...monoStyle, fontSize: "1rem", fontWeight: 600 }}>{result.ip}</span>
            {jailed ? <Pill tone="warn">jailed</Pill> : <Pill tone="info">not jailed</Pill>}
            {inIpsum && <Pill tone={result.ignored ? "muted" : "warn"}>{result.ignored ? "on the ipsum list, exempt" : "blocked by the ipsum list"}</Pill>}
            {result.ignored && <Pill tone="info">ignored</Pill>}
            <span style={{ flex: 1 }} />
            {jailed && <button type="button" className="btn btn-secondary" onClick={() => onUnban(result.ip, false)}>Unban</button>}
            {!result.ignored && <button type="button" className="btn btn-secondary" onClick={() => onUnban(result.ip, true)}>{jailed ? "Unban and ignore" : "Ignore"}</button>}
          </div>
          {result.ignoredBy.length > 0 && (
            <div style={{ fontSize: "0.85rem" }}>
              On the ignore list as {result.ignoredBy.map((i, n) => <span key={i.pbxIgnoreIpId}>{n > 0 && ", "}<span style={monoStyle}>{i.cidr}</span> ({i.comment}{i.hostname ? `, ${i.hostname} only` : ""})</span>)}
            </div>
          )}
          <div style={{ fontSize: "0.85rem" }}>
            <strong>Last registered as: </strong>
            {result.registeredFrom.length === 0 ? <span style={{ color: "var(--portal-muted)" }}>no extension's last registration came from this address</span>
              : result.registeredFrom.map((r, n) => <span key={r.agentId}>{n > 0 && ", "}<ExtensionLabel ext={r} /> ({r.customerName})</span>)}
          </div>
          {result.pbxs.map((p) => (
            <div key={p.pbxId} className="tile" style={{ gap: 8, padding: "12px 16px" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <strong>{p.hostname}</strong>
                {!p.ok && <span style={{ color: "#a12a1f" }}>{p.error}</span>}
                <span style={{ flex: 1 }} />
                <button type="button" className="btn btn-secondary" onClick={() => onLog("iplog", result.ip, p.pbxId)} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><FileText size={14} /> iplog</button>
              </div>
              {p.ok && (
                <>
                  <div style={{ fontSize: "0.85rem" }}>
                    {p.jails.length === 0 ? "In no jail." : p.jails.map((j) => (
                      <div key={j.jail}><Pill tone={j.jail === "recidive" ? "warn" : "info"}>{j.jail}</Pill> since {fmtUtc(j.bannedUtc)}, released {fmtIn(j.endsUtc)} ({fmtUtc(j.endsUtc)})</div>
                    ))}
                  </div>
                  <div style={{ fontSize: "0.85rem", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span>Registrations from this address (this and last week, × = failed):</span>
                    <UserChips users={p.users} otherUsers={p.otherUsers} />
                    {p.users.filter((u) => u.extension).map((u) => (
                      <button key={u.username} type="button" className="btn btn-secondary" onClick={() => onLog("userlog", u.username, p.pbxId)} style={{ padding: "2px 8px", fontSize: "0.78rem" }}>userlog {u.username}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- iplog / userlog --------------------------------------------------------------------

function LogsTab({ pbxs, tool, target, pbxId, onRun }: {
  pbxs: { pbxId: number; hostname: string }[];
  tool: LogTool;
  target: string;
  pbxId: number | null;
  onRun: (tool: LogTool, target: string, pbxId: number) => void;
}) {
  const [formTool, setFormTool] = useState<LogTool>(tool);
  const [formTarget, setFormTarget] = useState(target);
  const [formPbx, setFormPbx] = useState<number | null>(pbxId);
  const [all, setAll] = useState(false);
  const [summary, setSummary] = useState(false);
  const [result, setResult] = useState<ToolOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [runKey, setRunKey] = useState(0);

  const effectivePbx = formPbx ?? pbxs[0]?.pbxId ?? null;

  useEffect(() => { setFormTool(tool); setFormTarget(target); if (pbxId) setFormPbx(pbxId); }, [tool, target, pbxId]);

  useEffect(() => {
    if (!target || !(pbxId ?? pbxs[0]?.pbxId)) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    pbxBanlistApi.log({ pbxId: (pbxId ?? pbxs[0].pbxId), tool, target, all, summary: tool === "userlog" && summary })
      .then((r) => { if (!cancelled) setResult(r); })
      .catch((e) => { if (!cancelled) { setResult(null); setError(banlistError(e)); } })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
    // all/summary are read when a run starts; changing them alone does not rerun.
  }, [tool, target, pbxId, runKey, pbxs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!formTarget.trim() || effectivePbx == null) return;
    if (formTool === tool && formTarget.trim() === target && effectivePbx === pbxId) setRunKey((k) => k + 1);
    else onRun(formTool, formTarget.trim(), effectivePbx);
  };

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 12 }}>
      <form onSubmit={submit} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <SegmentedFilter value={formTool} options={[{ value: "iplog" as LogTool, label: "iplog (address)" }, { value: "userlog" as LogTool, label: "userlog (username)" }]} onChange={setFormTool} ariaLabel="Tool" />
        {pbxs.length > 1 && (
          <select value={effectivePbx ?? ""} onChange={(e) => setFormPbx(Number(e.target.value))} style={inputStyle} aria-label="PBX">
            {pbxs.map((p) => <option key={p.pbxId} value={p.pbxId}>{p.hostname}</option>)}
          </select>
        )}
        <input type="text" value={formTarget} onChange={(e) => setFormTarget(e.target.value)} placeholder={formTool === "iplog" ? "IP address" : "PBX username"} style={{ ...inputStyle, ...monoStyle, flex: "0 1 260px" }} aria-label="Target" />
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: "0.85rem" }} title="Also search the rotated (older) log files; slower">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Older logs too
        </label>
        {formTool === "userlog" && (
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: "0.85rem" }} title="Per-address summary from the fail2ban log">
            <input type="checkbox" checked={summary} onChange={(e) => setSummary(e.target.checked)} /> Summary per address
          </label>
        )}
        <button type="submit" className="btn btn-primary" disabled={busy || !formTarget.trim() || effectivePbx == null}>{busy ? "Running…" : "Run"}</button>
      </form>
      {error && <ErrorTile message={error} />}
      {result && (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>
            <span style={monoStyle}>{result.command}</span> on {result.hostname} · {(result.durationMs / 1000).toFixed(1)} s
            {result.exitCode !== 0 && <span style={{ color: "#a12a1f" }}> · exit {result.exitCode}{result.error ? `: ${result.error}` : ""}</span>}
            {result.truncated && " · only the newest lines are shown"}
          </div>
          <pre style={{ ...monoStyle, margin: 0, padding: 12, background: "var(--portal-surface)", border: "1px solid var(--portal-border)", borderRadius: 6, maxHeight: "calc(100vh - 420px)", minHeight: 160, overflow: "auto", whiteSpace: "pre", lineHeight: 1.45 }}>
            {result.output.trim() === "" ? <span style={{ color: "var(--portal-muted)" }}>No matches.</span> : result.output.split("\n").map((line, n) => (
              <div key={n} style={{ color: / Unban /.test(line) ? "#1e7d32" : / Ban /.test(line) ? "#a12a1f" : undefined, fontWeight: / (Un)?[Bb]an /.test(line) && /fail2ban\.actions/.test(line) ? 700 : undefined }}>{line || " "}</div>
            ))}
          </pre>
        </div>
      )}
    </div>
  );
}

// ---- ignore list ------------------------------------------------------------------------

function IgnoreTab({ pbxs }: { pbxs: { pbxId: number; hostname: string }[] }) {
  const [data, setData] = useState<IgnoreList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cidr, setCidr] = useState("");
  const [comment, setComment] = useState("");
  const [pbxId, setPbxId] = useState<number | null>(null);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; cidr: string } | null>(null);
  const [confirmDeploy, setConfirmDeploy] = useState(false);
  const [live, setLive] = useState<LiveIgnore[] | null>(null);
  const [message, setMessage] = useState<{ title: string; body: ReactNode } | null>(null);

  const load = useCallback(() => {
    pbxBanlistApi.ignore().then(setData).catch((e) => setError(banlistError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const isAdmin = data?.isAdmin ?? false;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!cidr.trim() || !comment.trim()) return;
    setBusy(true);
    try {
      const r = await pbxBanlistApi.addIgnore({ cidr: cidr.trim(), comment: comment.trim(), pbxId, force });
      setCidr("");
      setComment("");
      setForce(false);
      setMessage({ title: `${r.entry?.cidr ?? "Entry"} added`, body: <DeployLines deploys={r.deploys} /> });
      load();
      if (live) setLive(await pbxBanlistApi.liveIgnore());
    } catch (err) {
      setMessage({ title: "Not added", body: banlistError(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      const r = await pbxBanlistApi.deleteIgnore(confirmDelete.id);
      setMessage({ title: `${confirmDelete.cidr} removed`, body: <DeployLines deploys={r.deploys} /> });
      load();
    } catch (err) {
      setMessage({ title: "Not removed", body: banlistError(err) });
    } finally {
      setBusy(false);
      setConfirmDelete(null);
    }
  };

  const deployAll = async () => {
    setBusy(true);
    try {
      const r = await pbxBanlistApi.deploy(null);
      setMessage({ title: "Ignore list deployed", body: <DeployLines deploys={r} /> });
      load();
      setLive(await pbxBanlistApi.liveIgnore());
    } catch (err) {
      setMessage({ title: "Deploy failed", body: banlistError(err) });
    } finally {
      setBusy(false);
      setConfirmDeploy(false);
    }
  };

  const compare = async () => {
    setBusy(true);
    try { setLive(await pbxBanlistApi.liveIgnore()); } catch (err) { setError(banlistError(err)); } finally { setBusy(false); }
  };

  const importEntry = async (entry: string, hostname: string) => {
    setBusy(true);
    try {
      await pbxBanlistApi.addIgnore({ cidr: entry, comment: `Imported from ${hostname}`, force: true });
      load();
      setLive(await pbxBanlistApi.liveIgnore());
    } catch (err) {
      setMessage({ title: `${entry} not imported`, body: banlistError(err) });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorTile message={error} />;
  if (!data) return <div style={{ color: "var(--portal-muted)" }}>Loading the ignore list…</div>;
  const neverDeployed = data.pbxs.filter((p) => !p.deployed);

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 14 }}>
      <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
        Addresses on this list are never jailed (freeswitch-register, freeswitch-invite and recidive) and are not blocked by the ipsum list.
        Adding an address that is jailed releases it straight away.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {data.pbxs.map((p) => (
          <div key={p.pbxId} className="tile" style={{ padding: "8px 14px", gap: 2, borderColor: p.lastError ? "#e74c3c" : undefined }}>
            <div style={{ fontWeight: 600 }}>{p.hostname} · {p.entries} entries</div>
            <div style={{ fontSize: "0.78rem", color: p.lastError ? "#a12a1f" : "var(--portal-muted)" }}>
              {!p.deployed ? "Never deployed: the PBX still uses its own list"
                : p.lastError ? p.lastError
                : p.inSync ? `Applied ${p.appliedUtc ? fmtAgo(secondsSince(p.appliedUtc)) : ""}${p.appliedBy ? ` by ${p.appliedBy}` : ""}`
                : "Changes pending, retried every 5 minutes"}
            </div>
          </div>
        ))}
        {isAdmin && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" className="btn btn-secondary" onClick={compare} disabled={busy}>Compare with the PBXs</button>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmDeploy(true)} disabled={busy}>Deploy now</button>
          </div>
        )}
      </div>

      {isAdmin && live && (
        <div className="tile" style={{ gap: 8, padding: "12px 16px" }}>
          {live.map((l) => (
            <div key={l.pbxId} style={{ display: "grid", gap: 6, fontSize: "0.85rem" }}>
              <strong>{l.hostname}</strong>
              {!l.ok ? <span style={{ color: "#a12a1f" }}>{l.error}</span> : (
                <>
                  <div>
                    Live in the jails but not on this list{l.onPbxNotInList.length ? " (a deploy would remove these)" : ""}:{" "}
                    {l.onPbxNotInList.length === 0 ? "none" : l.onPbxNotInList.map((e) => (
                      <span key={e} style={{ display: "inline-flex", gap: 4, alignItems: "center", marginRight: 10 }}>
                        <span style={monoStyle}>{e}</span>
                        <button type="button" className="btn btn-secondary" onClick={() => importEntry(e, l.hostname)} disabled={busy} style={{ padding: "1px 6px", fontSize: "0.75rem" }}>Import</button>
                      </span>
                    ))}
                  </div>
                  <div>On this list but not live yet: {l.inListNotOnPbx.length === 0 ? "none" : <span style={monoStyle}>{l.inListNotOnPbx.join(", ")}</span>}</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={add} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input type="text" value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="1.2.3.4 or 1.2.3.0/24" style={{ ...inputStyle, ...monoStyle, flex: "0 1 200px" }} aria-label="Address or network" />
        <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Why (required)" maxLength={200} style={{ ...inputStyle, flex: "1 1 240px" }} aria-label="Comment" />
        {pbxs.length > 1 && (
          <select value={pbxId ?? ""} onChange={(e) => setPbxId(e.target.value ? Number(e.target.value) : null)} style={inputStyle} aria-label="PBX">
            <option value="">All PBXs</option>
            {pbxs.map((p) => <option key={p.pbxId} value={p.pbxId}>{p.hostname} only</option>)}
          </select>
        )}
        {isAdmin && (
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: "0.85rem" }} title="Networks wider than /24 (IPv6 /64), down to /8">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Allow a wide network
          </label>
        )}
        <button type="submit" className="btn btn-primary" disabled={busy || !cidr.trim() || !comment.trim()}>Add</button>
      </form>

      <ScrollShadow>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr>
              <Th>Address / network</Th>
              <Th>Comment</Th>
              {pbxs.length > 1 && <Th>PBX</Th>}
              <Th>Added by</Th>
              <Th>Added (UTC)</Th>
              <Th align="right"> </Th>
            </tr>
          </thead>
          <tbody>
            {data.entries.length === 0 && (
              <tr>
                <td colSpan={pbxs.length > 1 ? 6 : 5} style={{ padding: "9px 14px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)" }}>
                  {isAdmin ? "The ignore list is empty." : "You have not added any addresses yet."}
                </td>
              </tr>
            )}
            {data.entries.map((e) => (
              <tr key={e.pbxIgnoreIpId}>
                <Td><span style={monoStyle}>{e.cidr}</span>{e.isSystem && <span style={{ marginLeft: 8 }}><Pill tone="muted">infrastructure</Pill></span>}</Td>
                <Td><span style={{ whiteSpace: "normal" }}>{e.comment}</span></Td>
                {pbxs.length > 1 && <Td>{e.hostname ?? "All"}</Td>}
                <Td>{e.createdBy}{isAdmin && e.managerName ? <span style={{ color: "var(--portal-muted)" }}> ({e.managerName})</span> : null}</Td>
                <Td>{fmtUtc(e.createdUtc)}</Td>
                <Td align="right">
                  {e.canDelete && (
                    <button type="button" className="btn btn-secondary" title="Remove" aria-label="Remove" onClick={() => setConfirmDelete({ id: e.pbxIgnoreIpId, cidr: e.cidr })} style={{ padding: "4px 8px" }}><Trash2 size={14} /></button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollShadow>
      {!isAdmin && data.hiddenEntries > 0 && (
        <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>The list also holds {data.hiddenEntries} entries of the platform and other resellers.</div>
      )}

      <ConfirmModal open={confirmDelete != null} onClose={() => (busy ? undefined : setConfirmDelete(null))} title="Remove from the ignore list" confirmLabel="Remove" busy={busy} onConfirm={remove}>
        <p style={{ margin: 0 }}>Remove <span style={monoStyle}>{confirmDelete?.cidr}</span>? Failed registrations from it can get it jailed again.</p>
      </ConfirmModal>
      <ConfirmModal open={confirmDeploy} onClose={() => (busy ? undefined : setConfirmDeploy(false))} title="Deploy the ignore list" confirmLabel="Deploy" tone="caution" busy={busy} onConfirm={deployAll}>
        <p style={{ margin: 0 }}>
          Replace the ignore list of freeswitch-register, freeswitch-invite and recidive on every PBX with this list?
          {neverDeployed.length > 0 && <> {neverDeployed.map((p) => p.hostname).join(", ")} never had it: anything in its own list that is not here disappears. Use “Compare with the PBXs” first.</>}
        </p>
      </ConfirmModal>
      <MessageModal open={message != null} onClose={() => setMessage(null)} title={message?.title ?? ""}>
        <div>{message?.body}</div>
      </MessageModal>
    </div>
  );
}

// ---- activity ---------------------------------------------------------------------------

const ACTION_LABELS: Record<string, string> = {
  unban: "Unban",
  "ignore-add": "Ignore added",
  "ignore-remove": "Ignore removed",
  "ignore-deploy": "Ignore list deployed",
};

function ActivityTab() {
  const [rows, setRows] = useState<BanAction[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { pbxBanlistApi.actions().then(setRows).catch((e) => setError(banlistError(e))); }, []);
  if (error) return <ErrorTile message={error} />;
  if (!rows) return <div style={{ color: "var(--portal-muted)" }}>Loading…</div>;
  if (rows.length === 0) return <div style={{ color: "var(--portal-muted)" }}>Nothing has been unbanned or ignored yet.</div>;
  return (
    <div className="pbx-tab-panel">
      <ScrollShadow>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr><Th>When (UTC)</Th><Th>What</Th><Th>Address</Th><Th>PBX</Th><Th>Detail</Th><Th>By</Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pbxBanActionId}>
                <Td>{fmtUtc(r.createdUtc)}</Td>
                <Td>{ACTION_LABELS[r.action] ?? r.action}</Td>
                <Td><span style={monoStyle}>{r.target}</span></Td>
                <Td>{r.hostname ?? "All"}</Td>
                <Td><span style={{ whiteSpace: "normal" }}>{r.detail}</span></Td>
                <Td>{r.byName}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollShadow>
    </div>
  );
}
